// src/components/ui/FormSelect/FormSelect.tsx
import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {createPortal} from 'react-dom';
import {IonIcon} from '@ionic/react';
import {
    closeCircleOutline,
    addOutline,
    checkmarkOutline,
    chevronDownOutline,
} from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Select visual variants */
export type FormSelectVariant =
    | 'default'      // Filled background with border on focus
    | 'ghost'        // Borderless, shows border on hover/focus (no chevron)
    | 'underline';   // Only bottom border

/** Select sizes */
export type FormSelectSize = 'sm' | 'md' | 'lg';

/** Dropdown option type */
export interface SelectOption<T = string> {
    id: string;
    label: string;
    sublabel?: string;
    icon?: React.ReactNode;
    /** Logo URL for organizations/payees - takes precedence over icon */
    logoUrl?: string | null;
    value: T;
    disabled?: boolean;
    group?: string;
}

/** Addon slot content */
export interface FormSelectAddon {
    /** Icon (ionicons string) */
    icon?: string;
    /** iOS-specific icon (overrides icon on iOS) */
    ios?: string;
    /** Material Design icon (overrides icon on Android/web) */
    md?: string;
    /** Google Material Symbols icon name (e.g., "pin", "dialpad") */
    materialIcon?: string;
    /** Icon color */
    color?: string;
    /** Custom React node (overrides icon) */
    content?: React.ReactNode;
    /** Click handler */
    onClick?: () => void;
    /** Tooltip text */
    tooltip?: string;
    /** Visually hidden but accessible */
    ariaLabel?: string;
}

export interface FormSelectProps<T = string> {
    /** Current search/filter value */
    value: string;
    /** Change handler for search input */
    onChange: (value: string) => void;
    /** Placeholder text */
    placeholder?: string;
    /** Visual variant */
    variant?: FormSelectVariant;
    /** Size preset */
    size?: FormSelectSize;
    /** Leading addon (before input) */
    leadingAddon?: FormSelectAddon;
    /** Show clear button when value/selection exists */
    clearable?: boolean;
    /** Callback when cleared */
    onClear?: () => void;
    /** Disabled state */
    disabled?: boolean;
    /** Error state */
    error?: boolean;
    /** Error message */
    errorMessage?: string;
    /** Label above input */
    label?: string;
    /** Helper text below input */
    helperText?: string;
    /** Auto-focus on mount */
    autoFocus?: boolean;
    /** Input name */
    name?: string;
    /** Input id */
    id?: string;
    /** Aria label */
    'aria-label'?: string;
    /** Additional className */
    className?: string;
    /** Inline styles */
    style?: React.CSSProperties;

    // ─── Options & Selection ──────────────────────────────────────────

    /** Dropdown options */
    options: SelectOption<T>[];
    /** Callback when option selected */
    onSelect: (option: SelectOption<T>) => void;
    /** Currently selected option */
    selectedOption?: SelectOption<T> | null;
    /** Filter options based on input (default: true) */
    filterOptions?: boolean;
    /** Custom filter function */
    filterFn?: (option: SelectOption<T>, searchTerm: string) => boolean;
    /** No results message */
    noResultsMessage?: string;
    /** Show dropdown on focus (vs only when typing) */
    showDropdownOnFocus?: boolean;
    /** Enable search/filter functionality (default: true). When false, behaves like standard dropdown */
    searchable?: boolean;
    /** Max height of dropdown */
    dropdownMaxHeight?: number;
    /** Loading state for async options */
    loading?: boolean;
    /** Render custom option */
    renderOption?: (option: SelectOption<T>, isHighlighted: boolean) => React.ReactNode;
    /** Custom render for selected value */
    renderSelected?: (option: SelectOption<T>) => React.ReactNode;

    // ─── Creatable ────────────────────────────────────────────────────

    /** Allow creating new items */
    creatable?: boolean;
    /** Callback when creating new item */
    onCreate?: (value: string) => void;
    /** Create button label */
    createLabel?: string;

    // ─── Event Handlers ───────────────────────────────────────────────

    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

// ═══════════════════════════════════════════════════════════════════
// SIZE PRESETS
// ═══════════════════════════════════════════════════════════════════

interface SizePreset {
    height: number;
    fontSize: number;
    paddingX: number;
    iconSize: number;
    gap: number;
    labelSize: number;
    borderRadius: number;
}

const sizePresets: Record<FormSelectSize, SizePreset> = {
    sm: {
        height: 36,
        fontSize: 14,
        paddingX: 10,
        iconSize: 16,
        gap: 8,
        labelSize: 12,
        borderRadius: 8,
    },
    md: {
        height: 44,
        fontSize: 15,
        paddingX: 12,
        iconSize: 18,
        gap: 10,
        labelSize: 13,
        borderRadius: 10,
    },
    lg: {
        height: 52,
        fontSize: 17,
        paddingX: 14,
        iconSize: 20,
        gap: 12,
        labelSize: 14,
        borderRadius: 12,
    },
};

// ═══════════════════════════════════════════════════════════════════
// HELPER COMPONENTS
// ═══════════════════════════════════════════════════════════════════

/** Renders logo image or fallback letter/icon for an option */
const OptionVisual: React.FC<{
    option: SelectOption<unknown>;
    size: number;
    showFallbackIcon?: boolean;
    /** Use primary color for fallback (like PayeeLogo) */
    primary?: boolean;
}> = ({option, size, showFallbackIcon = true, primary = false}) => {
    const [imgError, setImgError] = useState(false);

    // If we have a valid logo URL and image hasn't errored, show the logo
    if (option.logoUrl && !imgError) {
        return (
            <img
                src={option.logoUrl}
                alt={`${option.label} logo`}
                onError={() => setImgError(true)}
                style={{
                    width: size,
                    height: size,
                    borderRadius: size > 24 ? 6 : 4,
                    objectFit: 'cover',
                    flexShrink: 0,
                }}
            />
        );
    }

    // Fallback to icon if provided
    if (option.icon && showFallbackIcon) {
        return <>{option.icon}</>;
    }

    // Letter fallback (like PayeeLogo) - show first letter of label
    return (
        <div style={{
            width: size,
            height: size,
            borderRadius: size > 24 ? 6 : 4,
            background: primary ? 'var(--kff-foundry-blue)' : 'var(--ion-color-step-150)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size > 24 ? 14 : 11,
            fontWeight: 600,
            fontFamily: 'var(--kff-heading-font)',
            color: primary ? '#fff' : 'var(--ion-color-medium)',
            flexShrink: 0,
        }}>
            {option.label.charAt(0).toUpperCase()}
        </div>
    );
};

const LoadingSpinner: React.FC<{size: number}> = ({size}) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        style={{animation: 'formselect-spin 1s linear infinite'}}
    >
        <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="31.4 31.4"
            strokeDashoffset="10"
            opacity="0.3"
        />
        <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="31.4 31.4"
            strokeDashoffset="50"
        />
        <style>{`
            @keyframes formselect-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        `}</style>
    </svg>
);

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function FormSelect<T = string>({
    value,
    onChange,
    placeholder = 'Search or select...',
    variant = 'ghost',
    size = 'md',
    leadingAddon,
    clearable = true,
    onClear,
    disabled = false,
    error = false,
    errorMessage,
    label,
    helperText,
    autoFocus = false,
    name,
    id,
    'aria-label': ariaLabel,
    className,
    style,
    // Options & Selection
    options,
    onSelect,
    selectedOption,
    filterOptions = true,
    filterFn,
    noResultsMessage = 'No results found',
    showDropdownOnFocus = true,
    searchable = true,
    dropdownMaxHeight = 280,
    loading = false,
    renderOption,
    renderSelected,
    // Creatable
    creatable = false,
    onCreate,
    createLabel = 'Create',
    // Event handlers
    onFocus,
    onBlur,
    onKeyDown,
}: FormSelectProps<T>) {
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [dropdownPosition, setDropdownPosition] = useState<{top: number; left: number; width: number}>({top: 0, left: 0, width: 0});
    // Prevent immediate re-opening after selection
    const justSelectedRef = useRef(false);

    const sizes = sizePresets[size];

    // Calculate dropdown position when showing
    useEffect(() => {
        if (showDropdown && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 2,
                left: rect.left,
                width: rect.width,
            });
        }
    }, [showDropdown]);
    const hasOptions = options && options.length > 0;
    const hasValue = value.length > 0;
    const hasSelection = !!selectedOption;
    const showClearButton = clearable && (hasValue || hasSelection) && !disabled;

    // Filter options (skip filtering when not searchable)
    const filteredOptions = useMemo(() => {
        if (!options) return [];
        if (!searchable || !filterOptions || !value) return options;

        const defaultFilter = (opt: SelectOption<T>, term: string) => {
            const searchTerm = term.toLowerCase();
            return (
                opt.label.toLowerCase().includes(searchTerm) ||
                opt.sublabel?.toLowerCase().includes(searchTerm)
            );
        };

        const filter = filterFn || defaultFilter;
        return options.filter(opt => filter(opt, value));
    }, [options, value, filterOptions, filterFn, searchable]);

    // Check if input matches existing option
    const matchesExisting = useMemo(() => {
        if (!options) return false;
        return options.some(opt => opt.label.toLowerCase() === value.toLowerCase());
    }, [options, value]);

    // Creatable disabled when not searchable (no typing = no creation)
    const canCreate = searchable && creatable && hasValue && !matchesExisting && onCreate;

    // Close dropdown on outside click (check both container and portal dropdown)
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const isInsideContainer = containerRef.current?.contains(target);
            const isInsideDropdown = dropdownRef.current?.contains(target);

            if (!isInsideContainer && !isInsideDropdown) {
                setShowDropdown(false);
                setHighlightedIndex(-1);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    // Reset highlight when options change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredOptions.length]);

    // Handle focus
    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        // Don't re-open dropdown if we just selected an option
        if (hasOptions && showDropdownOnFocus && !justSelectedRef.current) {
            setShowDropdown(true);
        }
        onFocus?.(e);
    }, [hasOptions, showDropdownOnFocus, onFocus]);

    // Handle blur
    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        // Delay to allow click on dropdown
        setTimeout(() => {
            if (!containerRef.current?.contains(document.activeElement)) {
                setIsFocused(false);
                setShowDropdown(false);
                setHighlightedIndex(-1);
            }
        }, 150);
        onBlur?.(e);
    }, [onBlur]);

    // Handle input change
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        onChange(newValue);
        if (hasOptions) {
            setShowDropdown(true);
        }
    }, [onChange, hasOptions]);

    // Handle clear
    const handleClear = useCallback(() => {
        onChange('');
        onClear?.();
        inputRef.current?.focus();
    }, [onChange, onClear]);

    // Handle option select
    const handleSelect = useCallback((option: SelectOption<T>, e?: React.MouseEvent) => {
        if (option.disabled) return;
        // Stop propagation to prevent container click from re-opening
        e?.stopPropagation();
        // Set flag to prevent immediate re-opening on focus
        justSelectedRef.current = true;
        setTimeout(() => { justSelectedRef.current = false; }, 300);
        setShowDropdown(false);
        setHighlightedIndex(-1);
        setIsFocused(false);
        // Call the callback after closing
        onSelect(option);
        // Blur the input to fully close
        inputRef.current?.blur();
    }, [onSelect]);

    // Handle create
    const handleCreate = useCallback((e?: React.MouseEvent) => {
        if (canCreate) {
            // Stop propagation to prevent container click from re-opening
            e?.stopPropagation();
            // Set flag to prevent immediate re-opening on focus
            justSelectedRef.current = true;
            setTimeout(() => { justSelectedRef.current = false; }, 300);
            setShowDropdown(false);
            setHighlightedIndex(-1);
            setIsFocused(false);
            // Call the callback after closing
            onCreate?.(value.trim());
            // Blur the input to fully close
            inputRef.current?.blur();
        }
    }, [canCreate, onCreate, value]);

    // For ghost variant with selection, don't allow editing - must clear first
    const ghostWithSelection = variant === 'ghost' && hasSelection;

    // Open dropdown
    const openDropdown = useCallback(() => {
        // Don't re-open if we just selected an option
        if (justSelectedRef.current) return;
        // Ghost variant with selection in searchable mode: don't allow opening dropdown (must clear first)
        // But in non-searchable mode, always allow opening (it's a standard dropdown)
        if (searchable && ghostWithSelection) return;
        if (!disabled && hasOptions) {
            setShowDropdown(prev => !prev);
            if (searchable) {
                inputRef.current?.focus();
            }
        }
    }, [disabled, hasOptions, ghostWithSelection, searchable]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown) {
            if ((e.key === 'ArrowDown' || e.key === 'Enter') && hasOptions) {
                setShowDropdown(true);
                e.preventDefault();
            }
            onKeyDown?.(e);
            return;
        }

        const totalItems = filteredOptions.length + (canCreate ? 1 : 0);

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => (prev + 1) % totalItems);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    if (canCreate && highlightedIndex === 0) {
                        handleCreate();
                    } else {
                        const optionIndex = canCreate ? highlightedIndex - 1 : highlightedIndex;
                        if (filteredOptions[optionIndex]) {
                            handleSelect(filteredOptions[optionIndex]);
                        }
                    }
                } else if (canCreate) {
                    handleCreate();
                }
                break;
            case 'Escape':
                setShowDropdown(false);
                setHighlightedIndex(-1);
                inputRef.current?.blur();
                break;
        }

        onKeyDown?.(e);
    }, [showDropdown, hasOptions, filteredOptions, canCreate, highlightedIndex, handleSelect, handleCreate, onKeyDown]);

    // Scroll highlighted option into view
    useEffect(() => {
        if (highlightedIndex >= 0 && dropdownRef.current) {
            const items = dropdownRef.current.querySelectorAll('[data-option]');
            const item = items[highlightedIndex] as HTMLElement;
            if (item) {
                item.scrollIntoView({block: 'nearest'});
            }
        }
    }, [highlightedIndex]);

    // ─── Variant Styles ───────────────────────────────────────────────

    const getContainerStyles = (): React.CSSProperties => {
        const base: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
            gap: sizes.gap,
            height: sizes.height,
            padding: `0 ${sizes.paddingX}px`,
            borderRadius: 0, // clean, professional look
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            // Ghost with selection in searchable mode: not clickable (must clear first)
            // Non-searchable: always clickable (standard dropdown)
            cursor: disabled ? 'not-allowed' : (searchable && ghostWithSelection) ? 'default' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            position: 'relative',
        };

        const borderColor = error
            ? 'var(--ion-color-danger)'
            : isFocused
                ? 'var(--kff-forge-gold)'
                : isHovered
                    ? 'var(--ion-color-step-400)'
                    : 'var(--ion-color-step-200)';

        switch (variant) {
            case 'default':
                return {
                    ...base,
                    paddingRight: sizes.paddingX + 4, // extra breathing room for chevron
                    background: isFocused
                        ? 'var(--ion-color-step-50)'
                        : 'var(--ion-color-step-100)',
                    border: `1.5px solid ${isFocused ? borderColor : 'transparent'}`,
                    boxShadow: isFocused ? '0 0 0 3px rgba(203, 160, 82, 0.1)' : undefined,
                };
            case 'ghost':
                return {
                    ...base,
                    background: 'transparent',
                    borderBottom: `1.5px solid ${isFocused || isHovered ? borderColor : 'transparent'}`,
                    paddingLeft: 0,
                    paddingRight: 0,
                };
            case 'underline':
                return {
                    ...base,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `2px solid ${borderColor}`,
                    paddingLeft: 0,
                    paddingRight: 0,
                };
            default:
                return base;
        }
    };

    // ─── Render ───────────────────────────────────────────────────────

    // Show selected display when we have a selection and not focused/searching
    // For ghost variant: always show selected display when there's a selection (must clear to edit)
    // For non-searchable: always show static display (selected value or placeholder)
    const showSelectedDisplay = !searchable
        ? true
        : ghostWithSelection
            ? true
            : (selectedOption && !isFocused && !showDropdown);

    return (
        <div className={className} style={{...style, position: 'relative'}}>
            {/* Label */}
            {label && (
                <label
                    htmlFor={id}
                    style={{
                        display: 'block',
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: sizes.labelSize,
                        fontWeight: 500,
                        color: error ? 'var(--ion-color-danger)' : 'var(--ion-color-medium)',
                        marginBottom: 6,
                    }}
                >
                    {label}
                </label>
            )}

            {/* Select Container */}
            <div
                ref={containerRef}
                style={getContainerStyles()}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={openDropdown}
                // For non-searchable, make container focusable and handle keyboard
                tabIndex={!searchable && !disabled ? 0 : undefined}
                role={!searchable ? 'combobox' : undefined}
                aria-expanded={!searchable ? showDropdown : undefined}
                aria-haspopup={!searchable ? 'listbox' : undefined}
                aria-disabled={!searchable ? disabled : undefined}
                onKeyDown={!searchable ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (disabled) return;

                    if (!showDropdown) {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setShowDropdown(true);
                        }
                        return;
                    }

                    const totalItems = filteredOptions.length;

                    switch (e.key) {
                        case 'ArrowDown':
                            e.preventDefault();
                            setHighlightedIndex(prev => (prev + 1) % totalItems);
                            break;
                        case 'ArrowUp':
                            e.preventDefault();
                            setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
                            break;
                        case 'Enter':
                        case ' ':
                            e.preventDefault();
                            if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                                handleSelect(filteredOptions[highlightedIndex]);
                            }
                            break;
                        case 'Escape':
                            e.preventDefault();
                            setShowDropdown(false);
                            setHighlightedIndex(-1);
                            break;
                    }
                } : undefined}
                onFocus={!searchable ? () => setIsFocused(true) : undefined}
                onBlur={!searchable ? () => {
                    setTimeout(() => {
                        if (!containerRef.current?.contains(document.activeElement)) {
                            setIsFocused(false);
                            setShowDropdown(false);
                            setHighlightedIndex(-1);
                        }
                    }, 150);
                } : undefined}
            >
                {/* Leading Addon - shows selected option's logo/icon when available */}
                {(leadingAddon || (selectedOption?.icon || selectedOption?.logoUrl)) && (
                    <div
                        onClick={e => {
                            e.stopPropagation();
                            // Ghost with selection in searchable mode: icon not clickable
                            if (searchable && ghostWithSelection) return;
                            // If addon has custom onClick, use that; otherwise open dropdown
                            if (leadingAddon?.onClick) {
                                leadingAddon.onClick();
                            } else {
                                openDropdown();
                            }
                        }}
                        title={leadingAddon?.tooltip}
                        aria-label={leadingAddon?.ariaLabel || 'Open dropdown'}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: leadingAddon?.color || 'var(--ion-color-medium)',
                            cursor: (searchable && ghostWithSelection) ? 'default' : 'pointer',
                            transition: 'color 0.15s',
                            marginLeft: 4, // breathing room on the left
                        }}
                    >
                        {/* Show selected option's logo/letter when showing selected display, otherwise show leadingAddon */}
                        {selectedOption && showSelectedDisplay ? (
                            <OptionVisual option={selectedOption} size={sizes.iconSize + 8} primary />
                        ) : leadingAddon?.content ? (
                            leadingAddon.content
                        ) : leadingAddon?.materialIcon ? (
                            <span
                                className="material-symbols-outlined"
                                style={{
                                    fontSize: sizes.iconSize,
                                    width: sizes.iconSize,
                                    height: sizes.iconSize,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    lineHeight: 1,
                                    color: leadingAddon.color,
                                }}
                            >
                                {leadingAddon.materialIcon}
                            </span>
                        ) : (leadingAddon?.icon || leadingAddon?.ios || leadingAddon?.md) ? (
                            <IonIcon
                                icon={leadingAddon.icon}
                                ios={leadingAddon.ios}
                                md={leadingAddon.md}
                                style={{
                                    fontSize: sizes.iconSize,
                                    width: sizes.iconSize,
                                    height: sizes.iconSize,
                                    color: leadingAddon.color,
                                }}
                            />
                        ) : null}
                    </div>
                )}

                {/* Selected Display or Search Input */}
                <div style={{flex: 1, display: 'flex', alignItems: 'center', minWidth: 0}}>
                    {showSelectedDisplay ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 0,
                            }}
                        >
                            {renderSelected && selectedOption ? (
                                renderSelected(selectedOption)
                            ) : selectedOption ? (
                                /* Icon now shows in leading addon slot */
                                <span
                                    style={{
                                        fontSize: sizes.fontSize,
                                        fontWeight: 600,
                                        fontFamily: 'var(--kff-data-font)',
                                        color: 'var(--ion-text-color)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {selectedOption.label}
                                </span>
                            ) : (
                                /* Placeholder when no selection (non-searchable mode) */
                                <span
                                    style={{
                                        fontSize: sizes.fontSize,
                                        fontWeight: 500,
                                        fontFamily: 'var(--kff-data-font)',
                                        color: 'var(--ion-color-medium)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {placeholder}
                                </span>
                            )}
                        </div>
                    ) : (
                        <input
                            ref={inputRef}
                            type="text"
                            value={value}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            disabled={disabled}
                            autoFocus={autoFocus}
                            name={name}
                            id={id}
                            aria-label={ariaLabel || label}
                            aria-invalid={error}
                            aria-describedby={errorMessage ? `${id}-error` : undefined}
                            aria-expanded={showDropdown}
                            aria-haspopup="listbox"
                            role="combobox"
                            autoComplete="off"
                            style={{
                                flex: 1,
                                width: '100%',
                                minWidth: 0,
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: sizes.fontSize,
                                fontWeight: 500,
                                color: 'var(--ion-text-color)',
                                padding: 0,
                                cursor: disabled ? 'not-allowed' : 'text',
                            }}
                        />
                    )}
                </div>

                {/* Loading Spinner */}
                {loading && (
                    <div style={{color: 'var(--ion-color-medium)', flexShrink: 0}}>
                        <LoadingSpinner size={sizes.iconSize} />
                    </div>
                )}

                {/* Inline Create Button */}
                {canCreate && !loading && (
                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            handleCreate();
                        }}
                        aria-label={`${createLabel} "${value.trim()}"`}
                        title={`${createLabel} "${value.trim()}"`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: 0,
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--kff-forge-gold)',
                            cursor: 'pointer',
                            transition: 'transform 0.15s, color 0.15s',
                        }}
                        onMouseEnter={e => {e.currentTarget.style.transform = 'scale(1.15)';}}
                        onMouseLeave={e => {e.currentTarget.style.transform = 'scale(1)';}}
                    >
                        <IonIcon icon={addOutline} style={{fontSize: sizes.iconSize + 2}} />
                    </button>
                )}

                {/* Clear Button - always visible for ghost with selection, show alongside create button when both apply */}
                {showClearButton && !loading && (
                    <button
                        type="button"
                        onClick={e => {
                            e.stopPropagation();
                            handleClear();
                        }}
                        aria-label="Clear selection"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            padding: 0,
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--ion-color-medium)',
                            cursor: 'pointer',
                            // Always show clear button for: ghost with selection, non-searchable with selection, or hover/focus
                            opacity: ghostWithSelection || (!searchable && hasSelection) || isHovered || isFocused ? 1 : 0,
                            transition: 'opacity 0.15s, color 0.15s',
                        }}
                        onMouseEnter={e => {e.currentTarget.style.color = 'var(--ion-color-danger)';}}
                        onMouseLeave={e => {e.currentTarget.style.color = 'var(--ion-color-medium)';}}
                    >
                        <IonIcon icon={closeCircleOutline} style={{fontSize: sizes.iconSize}} />
                    </button>
                )}

                {/* Dropdown Chevron - show for non-ghost variants OR when not searchable (standard dropdown) */}
                {!loading && !canCreate && (variant !== 'ghost' || !searchable) && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: 'var(--ion-color-medium)',
                            transition: 'transform 0.2s',
                            transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    >
                        <IonIcon icon={chevronDownOutline} style={{fontSize: sizes.iconSize}} />
                    </div>
                )}

                {/* Dropdown - Rendered via portal to avoid modal overflow clipping */}
                {showDropdown && (hasOptions || canCreate) && createPortal(
                    <div
                        ref={dropdownRef}
                        role="listbox"
                        style={{
                            position: 'fixed',
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                            maxHeight: dropdownMaxHeight,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            background: 'var(--ion-card-background)',
                            borderRadius: 8,
                            padding: 4,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15), 0 0 0 1px var(--ion-border-color)',
                            zIndex: 10000,
                        }}
                    >
                        {/* Create Option */}
                        {canCreate && (
                            <div
                                data-option
                                role="option"
                                onClick={(e) => handleCreate(e)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '8px 10px',
                                    cursor: 'pointer',
                                    background: highlightedIndex === 0 ? 'var(--ion-color-step-100)' : 'transparent',
                                    color: 'var(--kff-forge-gold)',
                                    fontFamily: 'var(--kff-data-font)',
                                    fontSize: 14,
                                    fontWeight: 500,
                                    transition: 'background 0.1s',
                                    overflow: 'hidden',
                                }}
                                onMouseEnter={() => setHighlightedIndex(0)}
                            >
                                <IonIcon icon={addOutline} style={{fontSize: 18, flexShrink: 0}} />
                                <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {createLabel} "{value.trim()}"
                                </span>
                            </div>
                        )}

                        {/* Options List */}
                        {filteredOptions.map((option, index) => {
                            const optionIndex = canCreate ? index + 1 : index;
                            const isHighlighted = highlightedIndex === optionIndex;
                            const isSelected = selectedOption?.id === option.id;

                            if (renderOption) {
                                return (
                                    <div
                                        key={option.id}
                                        data-option
                                        role="option"
                                        aria-selected={isSelected}
                                        onClick={(e) => handleSelect(option, e)}
                                        onMouseEnter={() => setHighlightedIndex(optionIndex)}
                                    >
                                        {renderOption(option, isHighlighted)}
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={option.id}
                                    data-option
                                    role="option"
                                    aria-selected={isSelected}
                                    onClick={(e) => handleSelect(option, e)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '8px 10px',
                                        cursor: option.disabled ? 'not-allowed' : 'pointer',
                                        background: isHighlighted ? 'var(--ion-color-step-100)' : 'transparent',
                                        opacity: option.disabled ? 0.5 : 1,
                                        transition: 'background 0.1s',
                                        overflow: 'hidden',
                                    }}
                                    onMouseEnter={() => !option.disabled && setHighlightedIndex(optionIndex)}
                                >
                                    <div style={{flexShrink: 0, display: 'flex', alignItems: 'center'}}>
                                        <OptionVisual option={option} size={24} />
                                    </div>
                                    <div style={{flex: 1, minWidth: 0, overflow: 'hidden'}}>
                                        <div
                                            style={{
                                                fontFamily: 'var(--kff-data-font)',
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: 'var(--ion-text-color)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {option.label}
                                        </div>
                                        {option.sublabel && (
                                            <div
                                                style={{
                                                    fontFamily: 'var(--kff-data-font)',
                                                    fontSize: 12,
                                                    color: 'var(--ion-color-medium)',
                                                    marginTop: 2,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {option.sublabel}
                                            </div>
                                        )}
                                    </div>
                                    {isSelected && (
                                        <IonIcon
                                            icon={checkmarkOutline}
                                            style={{
                                                fontSize: 16,
                                                color: 'var(--kff-forge-gold)',
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}

                        {/* No Results */}
                        {filteredOptions.length === 0 && !canCreate && (
                            <div
                                style={{
                                    padding: '16px 12px',
                                    textAlign: 'center',
                                    fontFamily: 'var(--kff-data-font)',
                                    fontSize: 13,
                                    color: 'var(--ion-color-medium)',
                                }}
                            >
                                {noResultsMessage}
                            </div>
                        )}
                    </div>,
                    document.body
                )}
            </div>

            {/* Error Message */}
            {error && errorMessage && (
                <div
                    id={`${id}-error`}
                    role="alert"
                    style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 12,
                        color: 'var(--ion-color-danger)',
                        marginTop: 6,
                    }}
                >
                    {errorMessage}
                </div>
            )}

            {/* Helper Text */}
            {helperText && !error && (
                <div
                    style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 12,
                        color: 'var(--ion-color-medium)',
                        marginTop: 6,
                    }}
                >
                    {helperText}
                </div>
            )}
        </div>
    );
}

FormSelect.displayName = 'FormSelect';

export default FormSelect;
